import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FileText,
  Sparkles,
  Target,
  TrendingUp,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  ArrowLeft,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  Zap,
  BookOpen,
  Link as LinkIcon,
  Image,
  Code
} from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import AdminLayout from "./AdminLayout";

const API = process.env.REACT_APP_BACKEND_URL;

const ContentOptimizer = () => {
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/pages`);
      setPages(res.data);
    } catch (err) {
      console.error("Error fetching pages:", err);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedPage) return;

    try {
      setLoading(true);
      
      // Get page content
      const pageContent = selectedPage.sections?.map(s => 
        JSON.stringify(s.content || {})
      ).join(" ") || "";

      const res = await axios.post(`${API}/api/admin/seo/analyze-page`, {
        title: selectedPage.title,
        content: pageContent,
        keyword: keyword,
        meta_description: selectedPage.seo_settings?.meta_description || "",
        slug: selectedPage.slug
      });
      setAnalysis(res.data);
    } catch (err) {
      console.error("Error analyzing page:", err);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-yellow-500";
    if (score >= 40) return "text-orange-500";
    return "text-red-500";
  };

  const getScoreBg = (score) => {
    if (score >= 80) return "bg-green-500";
    if (score >= 60) return "bg-yellow-500";
    if (score >= 40) return "bg-orange-500";
    return "bg-red-500";
  };

  const ScoreBar = ({ label, score, icon: Icon }) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-[#A1A1AA]" />}
          <span className="text-[#A1A1AA] text-sm">{label}</span>
        </div>
        <span className={`font-semibold ${getScoreColor(score)}`}>{score}</span>
      </div>
      <div className="h-2 bg-[#1A1A1A] rounded-full overflow-hidden">
        <div 
          className={`h-full ${getScoreBg(score)} transition-all duration-500`}
          style={{ width: `${score}%` }}
        />
      </div>
    </div>
  );

  const RecommendationCard = ({ rec }) => {
    const priorityColors = {
      high: "border-red-500/30 bg-red-500/5",
      medium: "border-yellow-500/30 bg-yellow-500/5",
      low: "border-blue-500/30 bg-blue-500/5"
    };

    const priorityIcons = {
      high: <AlertTriangle className="w-5 h-5 text-red-500" />,
      medium: <AlertTriangle className="w-5 h-5 text-yellow-500" />,
      low: <CheckCircle className="w-5 h-5 text-blue-500" />
    };

    return (
      <div className={`p-4 border rounded-lg ${priorityColors[rec.priority] || priorityColors.medium}`}>
        <div className="flex items-start gap-3">
          {priorityIcons[rec.priority]}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[#F5F5F0] font-medium">{rec.issue}</span>
              <span className="text-xs px-2 py-0.5 rounded bg-[#1A1A1A] text-[#A1A1AA]">{rec.category}</span>
            </div>
            <p className="text-[#A1A1AA] text-sm mb-2">{rec.suggestion}</p>
            {rec.impact && (
              <p className="text-xs text-[#a1a1aa]">Impact: {rec.impact}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link
            to="/admin/seo"
            className="p-2 border border-[rgba(212,175,55,0.2)] rounded hover:border-[#D4AF37] transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-[#D4AF37]" />
          </Link>
          <div>
            <h1 className="text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0] flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-green-500" />
              Content Optimizer
            </h1>
            <p className="text-[#A1A1AA] mt-1">
              AI-powered page analysis and optimization recommendations
            </p>
          </div>
        </div>

        {/* Page Selection */}
        <div className="border border-[rgba(212,175,55,0.2)] rounded-lg bg-[#0A0A0A] p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="md:col-span-2">
              <label className="block text-[#A1A1AA] text-sm mb-2">Select Page to Analyze</label>
              <select
                value={selectedPage?.id || ""}
                onChange={(e) => setSelectedPage(pages.find(p => p.id === e.target.value))}
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-3 rounded focus:border-[#D4AF37] focus:outline-none"
              >
                <option value="">Choose a page...</option>
                {pages.map(page => (
                  <option key={page.id} value={page.id}>
                    {page.title} (/{page.slug})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[#A1A1AA] text-sm mb-2">Target Keyword</label>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="e.g., luxury events"
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-3 rounded focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
          </div>
          <button
            onClick={handleAnalyze}
            disabled={loading || !selectedPage}
            className="flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-[#050505] font-semibold rounded hover:bg-[#B8962E] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Analyze Page
              </>
            )}
          </button>
        </div>

        {/* Analysis Results */}
        {analysis && !analysis.error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Overall Score */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              <div className="lg:col-span-1 border border-[rgba(212,175,55,0.3)] rounded-lg bg-[#0A0A0A] p-6 flex flex-col items-center justify-center">
                <span className="text-[#A1A1AA] text-sm mb-2">Overall SEO Score</span>
                <div className={`text-6xl font-bold ${getScoreColor(analysis.overall_score)}`}>
                  {analysis.overall_score}
                </div>
                <span className="text-[#a1a1aa] text-sm mt-1">out of 100</span>
              </div>

              <div className="lg:col-span-3 border border-[rgba(212,175,55,0.2)] rounded-lg bg-[#0A0A0A] p-6">
                <h3 className="text-[#F5F5F0] font-semibold mb-4">Score Breakdown</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ScoreBar label="Keyword Usage" score={analysis.scores?.keyword_usage || 0} icon={Target} />
                  <ScoreBar label="Content Quality" score={analysis.scores?.content_quality || 0} icon={FileText} />
                  <ScoreBar label="Readability" score={analysis.scores?.readability || 0} icon={BookOpen} />
                  <ScoreBar label="Technical SEO" score={analysis.scores?.technical_seo || 0} icon={Code} />
                  <ScoreBar label="Internal Linking" score={analysis.scores?.internal_linking || 0} icon={LinkIcon} />
                </div>
              </div>
            </div>

            {/* Title Analysis */}
            {analysis.title_analysis && (
              <div className="border border-[rgba(212,175,55,0.2)] rounded-lg bg-[#0A0A0A] p-6">
                <button
                  onClick={() => setExpandedSection(expandedSection === 'title' ? null : 'title')}
                  className="w-full flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-[#D4AF37]" />
                    <h3 className="text-[#F5F5F0] font-semibold">Title Analysis</h3>
                    <span className={`text-sm font-medium ${getScoreColor(analysis.title_analysis.score)}`}>
                      {analysis.title_analysis.score}/100
                    </span>
                  </div>
                  {expandedSection === 'title' ? (
                    <ChevronUp className="w-5 h-5 text-[#A1A1AA]" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#A1A1AA]" />
                  )}
                </button>
                
                {expandedSection === 'title' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-4 space-y-4"
                  >
                    <div className="p-3 bg-[#121212] rounded">
                      <span className="text-[#A1A1AA] text-sm">Current Title:</span>
                      <p className="text-[#F5F5F0]">{analysis.title_analysis.current}</p>
                    </div>
                    
                    {analysis.title_analysis.issues?.length > 0 && (
                      <div>
                        <span className="text-[#A1A1AA] text-sm">Issues:</span>
                        <ul className="mt-2 space-y-1">
                          {analysis.title_analysis.issues.map((issue, i) => (
                            <li key={i} className="flex items-center gap-2 text-red-400 text-sm">
                              <XCircle className="w-4 h-4" />
                              {issue}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {analysis.title_analysis.suggestions?.length > 0 && (
                      <div>
                        <span className="text-[#A1A1AA] text-sm">Suggestions:</span>
                        <div className="mt-2 space-y-2">
                          {analysis.title_analysis.suggestions.map((suggestion, i) => (
                            <div key={i} className="flex items-center gap-2 p-2 bg-green-500/10 border border-green-500/20 rounded">
                              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                              <span className="text-[#F5F5F0] text-sm">{suggestion}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
              </div>
            )}

            {/* Meta Description Analysis */}
            {analysis.meta_description_analysis && (
              <div className="border border-[rgba(212,175,55,0.2)] rounded-lg bg-[#0A0A0A] p-6">
                <button
                  onClick={() => setExpandedSection(expandedSection === 'meta' ? null : 'meta')}
                  className="w-full flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <Code className="w-5 h-5 text-purple-400" />
                    <h3 className="text-[#F5F5F0] font-semibold">Meta Description Analysis</h3>
                    <span className={`text-sm font-medium ${getScoreColor(analysis.meta_description_analysis.score)}`}>
                      {analysis.meta_description_analysis.score}/100
                    </span>
                  </div>
                  {expandedSection === 'meta' ? (
                    <ChevronUp className="w-5 h-5 text-[#A1A1AA]" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#A1A1AA]" />
                  )}
                </button>
                
                {expandedSection === 'meta' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-4 space-y-4"
                  >
                    <div className="p-3 bg-[#121212] rounded">
                      <span className="text-[#A1A1AA] text-sm">Current Meta:</span>
                      <p className="text-[#F5F5F0]">{analysis.meta_description_analysis.current || "Not set"}</p>
                    </div>
                    
                    {analysis.meta_description_analysis.suggestions?.length > 0 && (
                      <div>
                        <span className="text-[#A1A1AA] text-sm">AI Suggestions:</span>
                        <div className="mt-2 space-y-2">
                          {analysis.meta_description_analysis.suggestions.map((suggestion, i) => (
                            <div key={i} className="p-3 bg-purple-500/10 border border-purple-500/20 rounded">
                              <p className="text-[#F5F5F0] text-sm">{suggestion}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
              </div>
            )}

            {/* Content Analysis */}
            {analysis.content_analysis && (
              <div className="border border-[rgba(212,175,55,0.2)] rounded-lg bg-[#0A0A0A] p-6">
                <button
                  onClick={() => setExpandedSection(expandedSection === 'content' ? null : 'content')}
                  className="w-full flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <BookOpen className="w-5 h-5 text-blue-400" />
                    <h3 className="text-[#F5F5F0] font-semibold">Content Analysis</h3>
                  </div>
                  {expandedSection === 'content' ? (
                    <ChevronUp className="w-5 h-5 text-[#A1A1AA]" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#A1A1AA]" />
                  )}
                </button>
                
                {expandedSection === 'content' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-4"
                  >
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="p-3 bg-[#121212] rounded text-center">
                        <span className="text-2xl font-bold text-[#F5F5F0]">{analysis.content_analysis.word_count}</span>
                        <p className="text-[#A1A1AA] text-xs">Words</p>
                      </div>
                      <div className="p-3 bg-[#121212] rounded text-center">
                        <span className="text-2xl font-bold text-[#F5F5F0]">{analysis.content_analysis.keyword_density}</span>
                        <p className="text-[#A1A1AA] text-xs">Keyword Density</p>
                      </div>
                      <div className="p-3 bg-[#121212] rounded text-center">
                        <span className={`text-2xl font-bold ${getScoreColor(analysis.content_analysis.readability_score)}`}>
                          {analysis.content_analysis.readability_score}
                        </span>
                        <p className="text-[#A1A1AA] text-xs">Readability</p>
                      </div>
                      <div className="p-3 bg-[#121212] rounded text-center">
                        <span className="text-2xl font-bold text-[#F5F5F0]">
                          {analysis.content_analysis.heading_structure?.h1_count || 0}/{analysis.content_analysis.heading_structure?.h2_count || 0}/{analysis.content_analysis.heading_structure?.h3_count || 0}
                        </span>
                        <p className="text-[#A1A1AA] text-xs">H1/H2/H3</p>
                      </div>
                    </div>

                    {analysis.content_analysis.content_gaps?.length > 0 && (
                      <div className="mt-4">
                        <span className="text-[#A1A1AA] text-sm">Content Gaps to Address:</span>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {analysis.content_analysis.content_gaps.map((gap, i) => (
                            <span key={i} className="px-3 py-1 bg-orange-500/10 border border-orange-500/20 text-orange-400 text-sm rounded">
                              {gap}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
              </div>
            )}

            {/* Recommendations */}
            {analysis.recommendations?.length > 0 && (
              <div className="border border-[rgba(212,175,55,0.2)] rounded-lg bg-[#0A0A0A] p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Zap className="w-5 h-5 text-[#D4AF37]" />
                  <h3 className="text-[#F5F5F0] font-semibold">AI Recommendations</h3>
                  <span className="text-[#A1A1AA] text-sm">({analysis.recommendations.length} suggestions)</span>
                </div>
                <div className="space-y-3">
                  {analysis.recommendations.map((rec, i) => (
                    <RecommendationCard key={i} rec={rec} />
                  ))}
                </div>
              </div>
            )}

            {/* Quick Wins */}
            {analysis.quick_wins?.length > 0 && (
              <div className="border border-green-500/30 rounded-lg bg-green-500/5 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <h3 className="text-[#F5F5F0] font-semibold">Quick Wins</h3>
                </div>
                <ul className="space-y-2">
                  {analysis.quick_wins.map((win, i) => (
                    <li key={i} className="flex items-start gap-2 text-[#A1A1AA]">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      {win}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </motion.div>
        )}

        {/* Error State */}
        {analysis?.error && (
          <div className="border border-red-500/30 rounded-lg bg-red-500/10 p-6">
            <p className="text-red-400">{analysis.error}</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ContentOptimizer;
